import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpService } from './http.service';
import { BehaviorSubject, Observable, map, of, throwError } from 'rxjs';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';
import { navigationItems } from '../utils/navigations';
import { Minimal_RolesArray, Repair_RolesArray, Service_RolesArray } from '../utils/role';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  navBarItems: any = [];
  sideNavArray = [];

  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.isLoggedInSubject.asObservable();

  constructor(public http: HttpService, public storage: StorageService, private router: Router) { }

  apiBaseUrl = environment.apiBaseUrl;

  getOTP(data: any): Observable<any> {
    return this.http.post(`${this.apiBaseUrl}/v2/send-contractor-otp`, data);
  }

  login(data: any): Observable<any> {
    return this.http.post(`${this.apiBaseUrl}/v2/verify-contractor-otp`, data).pipe(
      map((resp: any) => {
        this.isLoggedInSubject.next(true);
        // Expected to include token + refresh_token from login response.
        this.storage.setItem('local', 'authData', resp.data);
        this.generateSideNav();
        return resp;
      })
    );
  }

  /**
   * POST /api/v2/refresh-token
   * Body: { refresh_token }
   * Updates local authData with the response data on success.
   */
  refreshToken(): Observable<any> {
    const authData = this.getAuthDataSync();
    const refresh_token = authData?.refresh_token;
    if (!refresh_token) {
      return throwError(() => new Error('Missing refresh_token'));
    }

    return this.http.post(`${this.apiBaseUrl}/v2/refresh-token`, { refresh_token }).pipe(
      map((resp: any) => {
        const nextAuthData = {
          ...(authData || {}),
          ...(resp?.data || {}),
        };
        this.storage.setItem('local', 'authData', nextAuthData);
        this.isLoggedInSubject.next(true);
        return resp;
      })
    );
  }

  getAuthDataSync(): any | null {
    try {
      const raw = localStorage.getItem('authData');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  forceLogout(): void {
    this.isLoggedInSubject.next(false);
    this.storage.clear('local');
    this.router.navigate(['/login']);
  }

  logout() {
    return this.http.get(`${this.apiBaseUrl}/logout`).pipe(
      map((resp: any) => {
        this.isLoggedInSubject.next(false);
        this.storage.removeItem('local', 'authData');
        this.storage.removeItem('local', 'appConfig');
      })
    );
  }

  isRider(data?: any): boolean {
    const auth = data ?? this.getAuthDataSync();
    return auth?.sub_type === 'RIDER';
  }

  isStoreOwner(data?: any): boolean {
    const auth = data ?? this.getAuthDataSync();
    return auth?.sub_type === 'ROWNER';
  }

  isRepairUser(data?: any): boolean {
    const auth = data ?? this.getAuthDataSync();
    return Repair_RolesArray.includes(auth?.sub_type);
  }

  /** FIXMEUP / AGENT / STAFF / CAB_RIDER, or any type this portal doesn't recognise. */
  isMinimalUser(data?: any): boolean {
    const auth = data ?? this.getAuthDataSync();
    return Minimal_RolesArray.includes(auth?.sub_type) || !Service_RolesArray.includes(auth?.sub_type);
  }

  /** Home route after login / unauthorized redirect. */
  getHomeRoute(data?: any): string {
    return this.isMinimalUser(data) ? '/help' : '/dashboard';
  }

  /** Store Front Banners: ROWNER + supermart store type only. */
  shouldShowStoreFrontBanners(data: any): boolean {
    if (!data) return false;
    const storeType = String(data.store_type ?? '').toLowerCase();
    return (
      data.sub_type === 'ROWNER' &&
      (storeType === 'suppermart' || storeType === 'supermart')
    );
  }

  private isStoreFrontBannersNav(navs: { routerLink?: string; id?: number }): boolean {
    return navs.routerLink === '/store-front-banners' || navs.id === 18;
  }

  /** Appearance: ROWNER + supermart store type only (same rule as store front banners). */
  private isAppearanceNav(navs: { routerLink?: string; id?: number }): boolean {
    return navs.routerLink === '/appearance' || navs.id === 20;
  }

  generateSideNav() {
    this.navBarItems = [];
    this.storage.getItem('local', 'authData').subscribe((data: any) => {
      if (data) {
        if (Service_RolesArray.includes(data.sub_type)) {
          navigationItems.map((navs: any) => {
            if (navs.roles.includes(data.sub_type)) {
              if (
                (this.isStoreFrontBannersNav(navs) || this.isAppearanceNav(navs)) &&
                !this.shouldShowStoreFrontBanners(data)
              ) {
                return;
              }
              this.navBarItems.push(navs);
            }
          });
          this.navBarItems.sort((a: any, b: any) => {
            return a.id - b.id;
          });
        }
      }
    });
  }

  getSideBar(): Observable<any> {
    this.navBarItems = new Set(this.navBarItems);
    return of(this.navBarItems);
  }
}
